import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CenterModal from "../../UI/Modal/CenterModal";
import useLanguageChange from "../../../hooks/useLanguageChange";
import { GLOBAL_MEETING_DATA, PublicVariables } from "../../../constants/PublicVariables";
import { setAudioError, setAudioErrorModal, setShowPopupForPlayAudio } from "../../../reducers/twilioEvents-reducer";
import { HELP_ARTICLE } from "../../../constants/LDConstants";
import { goToPlayStore } from "../../../shared/utility";
import { setPermissionPopupText } from "../../CommonModals/CommonUtils";
import { ERROR_TYPE } from "../../../constants/CallSettingConstants";
import { TRACK_SUBSCRIBE_FOR } from "../../../constants/Constants";
import { setIsAudioButtonClickDisable } from "../../../reducers/common-reducer";
import { LogFirebase } from "../../../services/firestore";
import { handleEndCallFunc, updateAudioStatus } from "../Room/roomFunction";

const AudioErrorModals = () => {
    const dispatch = useDispatch();
    const { audioErrorModal, audioError, showPopupForPlayAudio } = useSelector((state) => state.twilioEvents);
    const { forLocalRemoteCheck } = useSelector((state) => state.trackFailed);

    const localisedValues = useLanguageChange();
    const [isAudioPermissionPopup, setIsAudioPermissionPopup] = useState(false);
    const [isAudioDeviceNotFoundPopup, setIsAudioDeviceNotFoundPopup] = useState(false);
    const [isNotReadablePopup, setIsNotReadablePopup] = useState(false);
    const [subscriptionFailedPopup, setSubscriptionFailedPopup] = useState(false);

    useEffect(() => {
        if (audioErrorModal && audioError) {
            dispatch(setIsAudioButtonClickDisable(false)); // enable audio button(button click variable))
        }
        if (audioErrorModal === true && audioError === ERROR_TYPE.PERMISSION) {
            setIsAudioPermissionPopup(true);
        }
        if (audioErrorModal === true && audioError === ERROR_TYPE.NOT_FOUND) {
            setIsAudioDeviceNotFoundPopup(true);
        }
        if (audioErrorModal === true && [ERROR_TYPE.NOT_READABLE, ERROR_TYPE.TRACK_PUBLICATION_FAILED].includes(audioError)) {
            setIsNotReadablePopup(true);
        }
        if (audioErrorModal === true && audioError === ERROR_TYPE.TRACK_SUBSCRIPTION_FAILED) {
            setSubscriptionFailedPopup(true);
        }
    }, [audioErrorModal, audioError]);

    const openHelpArticle = () => {
        window.open(HELP_ARTICLE.BROWSER_SPECIFIC_SOLUTION_LINK);
    };

    const handleDownloadAppButton = () => {
        handleEndCallFunc();
        goToPlayStore();
        handleCloseNotReadableModal();
        handleCloseSubscriptionFailedPopup();
    };

    const audioHeader = (headerVal, closeModal) => {
        return (
            <>
                <h5 className='modal-title'>{headerVal}</h5>
                <button type='button' className='btn-close' aria-label='Close' onClick={() => closeModal()}>
                    <span className='btn-icon icon-close-light'></span>
                </button>
            </>
        );
    };

    const audioFooter = (closeBtnVal, closeModal, actionBtnVal, actionFun, showActionBtn = true) => {
        return (
            <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
                <div className='button'>
                    <button className='btn default-btn-link' onClick={() => closeModal()}>
                        {closeBtnVal}
                    </button>
                </div>
                {showActionBtn && (
                    <div className='button'>
                        <button className='btn default-btn-primary' onClick={() => actionFun()}>
                            {actionBtnVal}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    //#region microphone device not found pop up
    const handleCloseDeviceNotFoundModal = () => {
        setIsAudioDeviceNotFoundPopup(false);
        dispatch(setAudioError(null));
        dispatch(setAudioErrorModal(false));
    };

    const handleMobileAppButton = () => {
        handleEndCallFunc();
        goToPlayStore();
        handleCloseDeviceNotFoundModal();
    };

    const customText = useMemo(() => {
        return setPermissionPopupText();
    }, [isAudioDeviceNotFoundPopup]);

    const deviceNotFoundBody = useMemo(() => {
        return (
            <div className='cms-modal-text' data-testid='device-not-found'>
                {!PublicVariables.isMobile && <p>{localisedValues.enable_or_attach_microphone_and_reconnect_call}</p>}
                {PublicVariables.isMobile && (
                    <>
                        <p>
                            {localisedValues.we_cannot_access_your_microphone} <span dangerouslySetInnerHTML={customText}></span> <br />
                            <span>{localisedValues.still_facing_issue}</span> <span>{localisedValues.try_the}</span>{" "}
                            <button className='btn default-btn-link' onClick={() => handleMobileAppButton()}>
                                {localisedValues.mobile_app}
                            </button>
                        </p>
                        {GLOBAL_MEETING_DATA.isAgent && (
                            <p>
                                <strong>{localisedValues.note_colon}</strong>{" "}
                                <span>{localisedValues.host_features_will_not_be_available_when_using_app}</span>
                            </p>
                        )}
                    </>
                )}
            </div>
        );
    }, [isAudioDeviceNotFoundPopup]);

    //#endregion microphone device not found pop up

    //#region permssion popup

    const handleClosePermissionModal = () => {
        setIsAudioPermissionPopup(false);
        dispatch(setAudioError(null));
        dispatch(setAudioErrorModal(false));
    };

    const permissionBody = useMemo(() => {
        return (
            <div className='cms-modal-text' data-testid='audio-permission-model'>
                <p>
                    <span>{localisedValues.we_cannot_access_your_microphone}</span> <span dangerouslySetInnerHTML={customText}></span>
                </p>
                {PublicVariables.isMobile && (
                    <p>
                        <span>{localisedValues.still_facing_issue}</span> <span>{localisedValues.try_the}</span>{" "}
                        <button className='btn default-btn-link' onClick={() => handleMobileAppButton()}>
                            {localisedValues.mobile_app}
                        </button>
                    </p>
                )}

                {GLOBAL_MEETING_DATA.isAgent && PublicVariables.isMobile && (
                    <p>
                        <strong>{localisedValues.note_colon}</strong>{" "}
                        <span>{localisedValues.host_features_will_not_be_available_when_using_app}</span>
                    </p>
                )}
            </div>
        );
    }, [isAudioPermissionPopup]);

    //#endregion permission popup

    //#region not readable error

    const handleCloseNotReadableModal = () => {
        setIsNotReadablePopup(false);
        dispatch(setAudioError(null));
        dispatch(setAudioErrorModal(false));
    };

    const notReadablePopupBody = useMemo(() => {
        return (
            <div className='cms-modal-text' data-testid='not-readable-model'>
                <p>
                    <span>{localisedValues.microphone_publication_description}</span>{" "}
                    <span>{localisedValues.please_close_all_other_application}</span>
                </p>

                {GLOBAL_MEETING_DATA.isAgent && (
                    <p>
                        <strong>{localisedValues.note_colon}</strong>{" "}
                        <span>{localisedValues.host_features_will_not_be_available_when_using_app}</span>
                    </p>
                )}
            </div>
        );
    }, [isNotReadablePopup]);

    //#region Subscription failed popup

    const handleCloseSubscriptionFailedPopup = () => {
        dispatch(setAudioError(null));
        setSubscriptionFailedPopup(false);
        dispatch(setAudioErrorModal(false));
    };

    const subscriptionFailedPopupBody = useMemo(() => {
        return (
            <div className='cms-modal-text'>
                {forLocalRemoteCheck === TRACK_SUBSCRIBE_FOR.REMOTE && (
                    <p>
                        <span>{localisedValues?.unable_to_play_remote_audio} </span>
                        <span>{localisedValues?.please_install_our_app_to_continue}</span>
                    </p>
                )}
                {forLocalRemoteCheck === TRACK_SUBSCRIBE_FOR.LOCAL && (
                    <p>
                        <span>{localisedValues?.unable_to_playback_audio} </span>
                        <span>{localisedValues?.please_install_our_app_to_continue}</span>
                    </p>
                )}
                {GLOBAL_MEETING_DATA.isAgent && (
                    <p>
                        <strong>{localisedValues.note_colon}</strong>{" "}
                        <span>{localisedValues.host_features_will_not_be_available_when_using_app}</span>
                    </p>
                )}
            </div>
        );
    }, [subscriptionFailedPopup]);

    const playRemoteParticipantAudio = () => {
        /* Get all audio elements within the element with ID 'audioTag' */
        const audioElements = document.getElementById("audioTag")?.querySelectorAll("audio") || [];
        audioElements?.forEach((e) => e.play());
        updateAudioStatus(200);
        dispatch(setShowPopupForPlayAudio(false));
        LogFirebase("Remote Audio", "audio_paused", "true");
    };

    const playAudioErrorBody = useMemo(() => {
        return (
            <div className='cms-modal-text'>
                <p>{localisedValues?.remote_participant_voice_not_coming_description}</p>
                <p>
                    <button className='btn default-btn-link' onClick={playRemoteParticipantAudio}>
                        {localisedValues?.click_here_for_audio_label}
                    </button>
                    {localisedValues?.workaround_action_for_remote_audio_not_coming?.replace("<click_here>", "")}
                </p>
            </div>
        );
    }, [showPopupForPlayAudio]);

    const playAudioErrorFooter = useMemo(() => {
        return (
            <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
                <div className='button'>
                    <button className='btn default-btn-primary' onClick={playRemoteParticipantAudio}>
                        {localisedValues?.dont_show_again}
                    </button>
                </div>
            </div>
        );
    }, [showPopupForPlayAudio]);

    //#endregion Subscription failed popup
    return (
        <>
            <CenterModal
                showModal={isAudioDeviceNotFoundPopup}
                closeModal={() => handleCloseDeviceNotFoundModal()}
                modalClass='default-modal modal-small mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={audioHeader(localisedValues?.microphone_not_found, handleCloseDeviceNotFoundModal)}
                body={deviceNotFoundBody}
                footer={audioFooter(localisedValues?.close, handleCloseDeviceNotFoundModal, localisedValues.view_help_article, openHelpArticle)}
                backdrop={"static"}
            />

            <CenterModal
                showModal={isAudioPermissionPopup}
                closeModal={() => handleClosePermissionModal()}
                modalClass='default-modal modal-small mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={audioHeader(localisedValues?.microphone_permission_required_title, handleClosePermissionModal)}
                body={permissionBody}
                footer={audioFooter(localisedValues?.close, handleClosePermissionModal, localisedValues.view_help_article, openHelpArticle)}
                backdrop={"static"}
            />

            <CenterModal
                showModal={isNotReadablePopup}
                closeModal={() => handleCloseNotReadableModal()}
                modalClass='default-modal modal-small mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={audioHeader(localisedValues?.audio_unsupported, handleCloseNotReadableModal)}
                body={notReadablePopupBody}
                footer={audioFooter(localisedValues?.okay_cap, handleCloseNotReadableModal, localisedValues?.download_app, handleDownloadAppButton)}
                backdrop={"static"}
            />
            {/* subscription failed popup -- Audio not playing in local device */}
            <CenterModal
                showModal={subscriptionFailedPopup}
                closeModal={() => handleCloseSubscriptionFailedPopup()}
                modalClass='default-modal modal-small mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={audioHeader(localisedValues?.audio_unsupported, handleCloseSubscriptionFailedPopup)}
                body={subscriptionFailedPopupBody}
                footer={audioFooter(
                    localisedValues?.okay_cap,
                    handleCloseSubscriptionFailedPopup,
                    localisedValues?.download_app,
                    handleDownloadAppButton
                )}
                backdrop={"static"}
            />
            <CenterModal
                showModal={showPopupForPlayAudio}
                closeModal={() => playRemoteParticipantAudio()}
                modalClass='default-modal modal-small mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={audioHeader(localisedValues?.having_audio_issue, playRemoteParticipantAudio)}
                body={playAudioErrorBody}
                footer={playAudioErrorFooter}
                backdrop={"static"}
            />
        </>
    );
};

export default AudioErrorModals;
